@if (animal) {
  <div class="grid grid-cols-1 lg:grid-cols-[17rem_minmax(0,1fr)] gap-4 items-start">
    <aside class="flex flex-col gap-4">
      <nav class="flex flex-col gap-1" [attr.aria-label]="'animalMedical.headline' | transloco">
        @for (item of sections; track item.key) {
          <button
            type="button"
            class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors cursor-pointer border"
            [ngClass]="section === item.key
              ? 'bg-[var(--p-primary-50)] border-[var(--p-primary-200)] text-[var(--p-primary-800)] font-semibold'
              : 'border-transparent hover:bg-gray-100'"
            [attr.aria-current]="section === item.key ? 'page' : null"
            [attr.data-cy]="'medical-section-' + item.key"
            (click)="selectSection(item.key)"
          >
            <span class="flex items-center gap-2">
              <i [class]="item.icon"></i>
              {{ item.label | transloco }}
            </span>
            <span class="flex items-center gap-1">
              @if (item.key === 'prophylaxis' && countFor('prophylaxisAlerts') > 0) {
                <p-tag severity="danger" [rounded]="true" [value]="'' + countFor('prophylaxisAlerts')"
                       [pTooltip]="'animalMedical.sections.alertsHint' | transloco" />
              }
              <p-tag severity="secondary" [rounded]="true" [value]="'' + countFor(item.count)" />
            </span>
          </button>
        }
      </nav>

      <app-medical-veterinary-card [animal]="animal" />
    </aside>

    <section class="min-w-0 flex flex-col gap-3">
      @if (dueBanner) {
        <p-message severity="error" styleClass="w-full">
          <div class="flex flex-wrap items-center justify-between gap-3 w-full">
            <div class="flex flex-col">
              <span class="font-semibold">
                {{ 'animalMedical.banner.due' | transloco: { overdue: dueBanner.overdue, due: dueBanner.due } }}
              </span>
              @if (dueBanner.oldest) {
                <span class="text-sm">
                  {{ 'animalMedical.banner.oldestOverdue' | transloco: {
                    label: (dueBanner.oldest.type ? (getTypeLabelKey(dueBanner.oldest.type) | transloco) : (getCategoryLabelKey(dueBanner.oldest.category) | transloco)),
                    date: (dueBanner.oldest.dueAt | date:'dd.MM.yyyy')
                  } }}
                </span>
              }
            </div>
            @if (section !== 'prophylaxis') {
              <p-button size="small" [text]="true" icon="pi pi-arrow-right" iconPos="right"
                        [label]="'animalMedical.banner.toProphylaxis' | transloco"
                        (onClick)="selectSection('prophylaxis')" />
            }
          </div>
        </p-message>
      }

      @if (quarantineBanner) {
        <p-message severity="warn" styleClass="w-full">
          <div class="flex flex-wrap items-center justify-between gap-3 w-full">
            <div class="flex flex-col">
              <span class="font-semibold">
                {{ (quarantineBanner.blocked ? 'animalMedical.banner.quarantineBlocked' : 'animalMedical.banner.quarantineActive') | transloco }}
              </span>
              @if (quarantineBanner.quarantine; as quarantine) {
                <span class="text-sm">
                  @if (quarantine.endsAt) {
                    {{ 'animalMedical.banner.quarantinePeriod' | transloco: {
                      from: (quarantine.startsAt | date:'dd.MM.yyyy'),
                      to: (quarantine.endsAt | date:'dd.MM.yyyy'),
                      days: quarantineBanner.remainingDays
                    } }}
                  } @else {
                    {{ 'animalMedical.banner.quarantineUnlimited' | transloco: { from: (quarantine.startsAt | date:'dd.MM.yyyy') } }}
                  }
                </span>
              } @else if (animal.adoptionBlocked) {
                <span class="text-sm">
                  @if (animal.adoptionBlockedUntil) {
                    {{ 'animalMedical.banner.blockedUntil' | transloco: { to: (animal.adoptionBlockedUntil | date:'dd.MM.yyyy') } }}
                  } @else {
                    {{ 'animalMedical.untilFurtherNotice' | transloco }}
                  }
                </span>
              }
            </div>
            @if (section !== 'quarantine') {
              <p-button size="small" [text]="true" icon="pi pi-arrow-right" iconPos="right"
                        [label]="'animalMedical.banner.toQuarantine' | transloco"
                        (onClick)="selectSection('quarantine')" />
            }
          </div>
        </p-message>
      }

      @if (conditionsBanner) {
        <p-message [severity]="conditionsBanner.severe ? 'warn' : 'info'" styleClass="w-full">
          <div class="flex flex-wrap items-center justify-between gap-3 w-full">
            <div class="flex flex-col">
              <span class="font-semibold">
                {{ 'animalMedical.banner.activeConditions' | transloco: { count: conditionsBanner.conditions.length } }}
              </span>
              <span class="text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
                @for (condition of conditionsBanner.conditions; track condition.id) {
                  <span>
                    {{ getConditionKindLabelKey(condition.kind) | transloco }}: {{ condition.label }}
                    @if (condition.visibleToAdopter) {
                      <i class="pi pi-eye text-xs" [pTooltip]="'animalMedical.fields.visibleToAdopterShort' | transloco"></i>
                    }
                  </span>
                }
              </span>
              @if (conditionsBanner.visibleToAdopter) {
                <span class="text-xs">
                  {{ 'animalMedical.banner.conditionsVisibleToAdopter' | transloco: { count: conditionsBanner.visibleToAdopter } }}
                </span>
              }
            </div>
            @if (section !== 'conditions') {
              <p-button size="small" [text]="true" icon="pi pi-arrow-right" iconPos="right"
                        [label]="'animalMedical.banner.toConditions' | transloco"
                        (onClick)="selectSection('conditions')" />
            }
          </div>
        </p-message>
      }

      @if (loading && !counts?.records) {
        <div class="flex items-center gap-2 text-gray-500 p-4">
          <i class="pi pi-spinner pi-spin"></i>
          <span>{{ 'animalMedical.loading' | transloco }}</span>
        </div>
      }

      @switch (section) {
        @case ('records') {
          <app-medical-record-list
            [animal]="animal"
            [highlightRecordId]="highlightRecordId"
            (create)="openCreate()"
            (correct)="openCorrection($event)"
            (openFile)="previewFile = $event"
          />
        }
        @case ('prophylaxis') {
          <app-medical-prophylaxis (goToRecord)="goToRecord($event)" />
        }
        @case ('conditions') {
          <app-medical-conditions [animal]="animal" (goToRecord)="goToRecord($event)" />
        }
        @case ('quarantine') {
          <app-medical-quarantine [animal]="animal" />
        }
        @case ('attachments') {
          <app-medical-attachments (goToRecord)="goToRecord($event)" (openFile)="previewFile = $event" />
        }
      }
    </section>
  </div>

  @if (dialogVisible) {
    <app-medical-record-dialog
      [animal]="animal"
      [correctionOf]="correctionOf"
      (closed)="onDialogClosed()"
      (openFile)="previewFile = $event"
    />
  }

  <app-medical-file-preview [file]="previewFile" [animal]="animal" (closed)="previewFile = null" />
}
